import { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';

import { notify } from '../../app';
import { Jessica } from '../../app';
import { ProgressBar } from '../../components';
import { YoutubePlayer } from '../../components';
import { NavLabels } from '../../app/Routes/const';
import { useCourseDetails } from './useCourseDetails';
import { ArrowRight } from '../../assets/icons/ArrowRight';
import { Breadcrumbs, FinishedModal } from '../../components';
import {
  Overview,
  VideoTranscribe,
  QuizStep,
  MindMapCourse,
} from './components';
import { TabNames, tabs } from './const';
import { MobileLayout } from './Layouts/Mobile';
import { DesktopLayout } from './Layouts/Desktop';
import { getCourseLanguage } from '../../utilities';
import { Tabs, Tab, Button, SectionHeader } from '../../components';
import {
  useAuth,
  useIsMobile,
  useLocalize,
  useUpdateEffect,
} from '../../hooks';
import { SideNav, TestMindMap } from './components';
import type { CourseType, EnrollmentType } from './useCourse';

type CourseComponentProps = {
  course: CourseType;
  enrollment: EnrollmentType;
  onRefresh: () => void;
};

// TODO(Kuriy): it's a temporary solution for the test purpose
const testUsers = [
  'roman.kuriy@spsoft.com',
  'dmytro.skorobrekha@spsoft.com',
  'andriy.senyk@spsoft.com',
  'anton.karachansky@spsoft.com',
];

export const CourseComponent: React.FC<CourseComponentProps> = ({
  course,
  enrollment,

  onRefresh,
}) => {
  const {
    stepId,
    stepDetails,
    loading,
    updating,
    hasNext,

    handleSetStep,
    handleNextLesson,
    handleStartQuiz,
    handleCompleteStep,

    isFinishOpened,
    onHideFinish,
  } = useCourseDetails(course, enrollment, onRefresh);

  const { user } = useAuth();
  const { t } = useLocalize();
  const isMobile = useIsMobile();
  const [fullScreenMode, setFullScreenMode] = useState(false);
  const [activeTab, setActiveTab] = useState<TabNames>(
    isMobile ? TabNames.Course : TabNames.Overview
  );

  // TODO(Kuriy): it's a temporary solution for the test purpose
  const filteredTabs = useMemo(
    () =>
      tabs.filter(t => !t.permission || testUsers.includes(user?.name || '')) ||
      [],
    [user]
  );

  const tabsContent = [
    {
      name: 'overview',
      render: (content: string) => <Overview content={content} />,
    },
    {
      name: 'mindMap',
      render: () => (
        <MindMapCourse
          fullScreenMode={fullScreenMode}
          onFullScreen={() => setFullScreenMode(!fullScreenMode)}
          mindMap={stepDetails?.mindMap}
          loading={loading}
        />
      ),
    },
  ];

  if (stepDetails?.transcription) {
    tabsContent.push({
      name: 'videoTranscribe',
      render: () => <VideoTranscribe text={stepDetails?.transcription} />,
    });
  }

  if (testUsers.includes(user?.name || '')) {
    tabsContent.push({
      name: 'TestMindMap',
      render: () => <TestMindMap />,
    });
  }

  const handleTabChange = useCallback((tabName: TabNames) => {
    setActiveTab(tabName);
    if (tabName !== TabNames.MindMap) {
      setFullScreenMode(false);
    }
  }, []);

  useUpdateEffect(() => {
    setActiveTab(isMobile ? TabNames.Course : TabNames.Overview);
  }, [isMobile]);

  const isFullMindMap = useMemo(
    () => fullScreenMode && activeTab === TabNames.MindMap,
    [activeTab, fullScreenMode]
  );
  const language = getCourseLanguage(course);
  const Component = isMobile ? MobileLayout : DesktopLayout;
  const button = !!hasNext && (
    <Button
      size="small"
      disabled={updating}
      icon={<ArrowRight />}
      label={t('course.page.next')}
      onClick={() => handleNextLesson()}
      className="flex-row-reverse bg-main border-transparent hover:bg-goToPracticeHover hover:border-transparent"
    />
  );

  const navigation = (
    <SideNav
      course={course}
      enrollment={enrollment}
      stepId={stepId}
      updating={updating}
      collapsable={!isMobile}
      onChangeStep={handleSetStep}
    />
  );

  const agentId = stepDetails?.agentId || course?.agentId;

  return (
    <>
      <FinishedModal
        course={course}
        isOpened={isFinishOpened}
        onClose={onHideFinish}
      />
      <Component
        nextButton={isMobile ? button : null}
        title={
          !isMobile && (
            <SectionHeader
              className="mt-md flex flex-row justify-between items-center"
              labelClassName="w-2/3"
              label={
                <h1 className="flex flex-row gap-x-md">{stepDetails?.title}</h1>
              }
              content={button}
            />
          )
        }
        navigation={navigation}
        breadcrumbs={
          <Breadcrumbs
            subtitleUrl="/courses"
            subtitle={t(NavLabels.Courses)}
            title={course.title ?? 'Title'}
          />
        }
        progressBar={
          <div className="w-[24rem]">
            <ProgressBar
              height="h-1.5"
              className="bg-lightGray"
              containerClassName="p-0 flex-col"
              progressPercent={enrollment.progressPercent!}
            />
          </div>
        }
        loading={loading}
        tabs={
          <Tabs
            tabs={filteredTabs.map(tab => ({
              ...tab,
              label: t(tab.label),
            }))}
            className="mt-md"
            tabsClassName="border-b-transparent"
            tabClassName="border-main border-b-2 font-semibold py-0 px-0 cursor-pointer transition-def py-sm px-md text-medium font-medium border-solid"
            tabClassNameActive="text-main"
            tabClassNameNotActive="text-textSecondary"
            onChange={handleTabChange}
            activeTab={activeTab}
          >
            {tabsContent.map(({ render, name }) => (
              <Tab name={name} key={name}>
                {render(stepDetails?.description ?? '')}
              </Tab>
            ))}
          </Tabs>
        }
        stepDetails={stepDetails}
        video={
          !!stepDetails?.videoUrl?.length && (
            <div
              style={{
                display: `${isFullMindMap ? 'none' : 'block'}`,
              }}
            >
              <YoutubePlayer
                className={clsx('mb-md', isMobile ? '' : 'rounded-t-slug')}
                key={stepDetails?.videoUrl}
                onComplete={handleCompleteStep}
                source={stepDetails?.videoUrl ?? ''}
                onPlay={() => {
                  if ((window as any).AiAgentsWebWidget.isMicMuted?.()) return;

                  notify.info('Agent auto-muted');
                  (window as any).AiAgentsWebWidget.mute?.();
                }}
              />
            </div>
          )
        }
        quiz={
          !!stepId &&
          !!enrollment.id && (
            <QuizStep
              stepId={stepId}
              isMobile={isMobile}
              navigation={navigation}
              enrollmentId={enrollment.id}
              startQuiz={() => handleStartQuiz(stepId!)}
              isQuizFinished={!!enrollment.completedSteps?.includes(stepId!)}
            />
          )
        }
        jessica={
          !loading && (
            <Jessica
              key={agentId}
              agentId={agentId}
              language={language}
              enrollmentId={enrollment.id}
            />
          )
        }
      />
    </>
  );
};
